"use client";

import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import {
  type CheckReport,
  checkReferenceList,
  type EntryReport,
  type Finding,
} from "@/lib/citation-check-chicago";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const SAMPLE = `John Smith. "The effects of sleep on memory." Journal of Sleep 12, no. 3 (2020): 45-60.
Brown, Karen L. The Grammar of Rivers. New York: Academic Press, 2021.
Adams, Rachel, & Anna Doe. Birds of the Coast. Boston: Nature Press`;

function ConfidenceBadge({
  confidence,
  strings,
}: {
  confidence: Finding["confidence"];
  strings: Strings;
}) {
  const label =
    confidence === "high" ? strings.highConfidence : strings.worthALook;
  const cls =
    confidence === "high"
      ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
      : "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200";
  return (
    <span className={`rounded-full px-2 py-0.5 font-medium text-xs ${cls}`}>
      {label}
    </span>
  );
}

function CopyButton({ text, strings }: { text: string; strings: Strings }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleCopy() {
    const ok = await writeToClipboard(text);
    if (ok) {
      setCopied(true);
      setFailed(false);
      setTimeout(() => setCopied(false), 1500);
    } else {
      setFailed(true);
    }
  }

  return (
    <button
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground text-xs hover:bg-muted"
      onClick={handleCopy}
      type="button"
    >
      {copied ? (
        <Check aria-hidden="true" className="size-3.5" />
      ) : (
        <Copy aria-hidden="true" className="size-3.5" />
      )}
      {(() => {
        if (copied) {
          return strings.copied;
        }
        if (failed) {
          return strings.copyFailed;
        }
        return strings.copyFix;
      })()}
    </button>
  );
}

function FindingCard({
  finding,
  strings,
}: {
  finding: Finding;
  strings: Strings;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-foreground text-sm">{finding.title}</p>
        <ConfidenceBadge confidence={finding.confidence} strings={strings} />
      </div>
      <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
        {finding.message}
      </p>
      {finding.suggestion ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <code className="rounded bg-muted px-2 py-1 text-foreground text-xs">
            {finding.suggestion}
          </code>
          <CopyButton strings={strings} text={finding.suggestion} />
        </div>
      ) : null}
      <a
        className="mt-3 inline-block text-primary text-xs underline-offset-4 hover:underline"
        href={finding.learnMoreUrl}
        rel="noopener noreferrer"
        target="_blank"
      >
        {strings.whyLearnMore.replace("{label}", finding.learnMoreLabel)}
      </a>
    </div>
  );
}

function summaryLine(
  report: CheckReport,
  strings: Strings,
  locale: string
): string {
  const entries = tPlural(
    strings.entryCountPlural,
    locale,
    report.totalEntries
  );
  if (report.totalFindings === 0) {
    return strings.noIssuesAcrossEntries.replace("{entries}", entries);
  }
  return tPlural(
    strings.issuesToReviewAcrossEntries,
    locale,
    report.totalFindings,
    { entries }
  );
}

function EntryFindings({
  entry,
  strings,
}: {
  entry: EntryReport;
  strings: Strings;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-border border-l-4 border-l-amber-400 bg-card p-4">
        <p className="font-medium text-muted-foreground text-xs">
          {strings.entryPosition.replace("{position}", String(entry.position))}
        </p>
        <p className="mt-1 text-foreground text-sm leading-relaxed">
          {entry.text}
        </p>
      </div>
      <div className="space-y-3 pl-3">
        {entry.findings.map((finding) => (
          <FindingCard
            finding={finding}
            key={`${entry.position}-${finding.rule}`}
            strings={strings}
          />
        ))}
      </div>
    </div>
  );
}

function ResultsSection({
  report,
  strings,
  locale,
}: {
  report: CheckReport;
  strings: Strings;
  locale: string;
}) {
  const cleanEntries = report.entries.filter((e) => e.findings.length === 0);
  const flaggedEntries = report.entries.filter((e) => e.findings.length > 0);

  return (
    <section aria-live="polite" className="space-y-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.resultsHeading}
        </h2>
        <p className="text-muted-foreground text-sm">
          {summaryLine(report, strings, locale)}
        </p>
      </div>

      {report.totalFindings === 0 ? (
        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-muted-foreground text-sm leading-relaxed">
            {strings.noHighConfidenceIssues}
          </p>
        </div>
      ) : null}

      {report.listFindings.length > 0 ? (
        <div className="space-y-3">
          <h3 className="font-medium text-foreground text-sm">
            {strings.bibliographyListHeading}
          </h3>
          {report.listFindings.map((finding) => (
            <FindingCard
              finding={finding}
              key={finding.title}
              strings={strings}
            />
          ))}
        </div>
      ) : null}

      {flaggedEntries.map((entry) => (
        <EntryFindings entry={entry} key={entry.position} strings={strings} />
      ))}

      {report.totalFindings > 0 && cleanEntries.length > 0 ? (
        <p className="text-muted-foreground text-sm">
          {tPlural(strings.entriesWithoutIssues, locale, cleanEntries.length)}
        </p>
      ) : null}
    </section>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const CITATION_CHECKER_CHICAGO_STRINGS = {
  bibliographyToCheck: "Bibliography to check",
  pasteYourChicagoBibliographyHere:
    "Paste your Chicago bibliography here — one reference per line (or separate multi-line references with a blank line)...",
  tryASample: "Try a sample",
  clear: "Clear",
  whatThisCheckerLooksFor: "What this checker looks for",
  checkAuthors:
    "The first author inverted to {name} — while later authors stay in natural {naturalOrder} order.",
  checkAnd: "The word {and} (not {ampersand}) before the final author.",
  checkTitles:
    "Titles in headline-style {titleCase} rather than sentence case.",
  checkDoi: "DOIs as a clean {url} link.",
  checkAccessDate:
    "An {accessDate} on an entry that already has a publication year — Chicago reserves access dates for undated online sources.",
  checkList: "{alphabetical} of the list and {duplicate} entries.",
  lastNameFirstName: "Last name, First name",
  firstLast: "First Last",
  and: "“and”",
  text: "&",
  titleCase: "Title Case",
  httpsDoiOrg10Xxxx: "https://doi.org/10.xxxx",
  aTerminalPeriodUnlessThe:
    "A terminal period (unless the entry ends in a DOI or URL).",
  accessDate: "access date",
  alphabeticalOrder: "Alphabetical order",
  duplicate: "duplicate",
  everyFindingIsAdvisoryAnd:
    "Every finding is advisory and links to the relevant Chicago guideline. The checker is deterministic — it never uploads your text, never uses AI, and never auto-rewrites your references. It targets notes-bibliography bibliography entries only (footnotes, author-date reference lists, and other styles are not checked yet).",
  highConfidence: "High confidence",
  worthALook: "Worth a look",
  copied: "Copied",
  copyFailed: "Copy failed",
  copyFix: "Copy fix",
  whyLearnMore: "Why? {label} →",
  entryCountPlural: "one: {count} entry|other: {count} entries",
  noIssuesAcrossEntries: "No issues detected across {entries}",
  issuesToReviewAcrossEntries:
    "one: {count} likely issue to review across {entries}|other: {count} likely issues to review across {entries}",
  entryPosition: "Entry {position}",
  resultsHeading: "Results",
  noHighConfidenceIssues:
    "No high-confidence formatting issues were detected. This is a helpful first pass, not a guarantee — the checker does not verify that a source exists or that every nuance of Chicago style is correct, so always double-check against the manual or your assignment guidelines.",
  bibliographyListHeading: "Bibliography list",
  entriesWithoutIssues:
    "one: {count} entry had no detected issues.|other: {count} entries had no detected issues.",
} as const;

type Strings = typeof CITATION_CHECKER_CHICAGO_STRINGS;

export default function CitationCheckerChicagoWidget() {
  const [text, setText] = useState("");
  const report = checkReferenceList(text);
  const hasInput = text.trim().length > 0;

  const strings = useToolUi(CITATION_CHECKER_CHICAGO_STRINGS);
  const locale = useToolLocale();

  return (
    <>
      {/* Input */}
      <div className="space-y-3">
        <textarea
          aria-label={strings.bibliographyToCheck}
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          onChange={(e) => setText(e.target.value)}
          placeholder={strings.pasteYourChicagoBibliographyHere}
          value={text}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
            onClick={() => setText(SAMPLE)}
            type="button"
          >
            {strings.tryASample}
          </button>
          {hasInput ? (
            <button
              className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
              onClick={() => setText("")}
              type="button"
            >
              {strings.clear}
            </button>
          ) : null}
        </div>
      </div>
      {/* Results */}
      {hasInput ? (
        <ResultsSection locale={locale} report={report} strings={strings} />
      ) : null}
      {/* What it checks */}
      <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.whatThisCheckerLooksFor}
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground text-sm leading-relaxed">
          <li>
            {renderSlots(strings.checkAuthors, {
              name: <strong>{strings.lastNameFirstName}</strong>,
              naturalOrder: <strong>{strings.firstLast}</strong>,
            })}
          </li>
          <li>
            {renderSlots(strings.checkAnd, {
              and: <strong>{strings.and}</strong>,
              ampersand: <strong>{strings.text}</strong>,
            })}
          </li>
          <li>
            {renderSlots(strings.checkTitles, {
              titleCase: <strong>{strings.titleCase}</strong>,
            })}
          </li>
          <li>
            {renderSlots(strings.checkDoi, {
              url: <strong>{strings.httpsDoiOrg10Xxxx}</strong>,
            })}
          </li>
          <li>{strings.aTerminalPeriodUnlessThe}</li>
          <li>
            {renderSlots(strings.checkAccessDate, {
              accessDate: <strong>{strings.accessDate}</strong>,
            })}
          </li>
          <li>
            {renderSlots(strings.checkList, {
              alphabetical: <strong>{strings.alphabeticalOrder}</strong>,
              duplicate: <strong>{strings.duplicate}</strong>,
            })}
          </li>
        </ul>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.everyFindingIsAdvisoryAnd}
        </p>
      </section>
    </>
  );
}
